/**
 * Checklist badges (20261019140000). A member earns one by finishing a list
 * they were asked to follow: "Get ready" (every worker) → `ready`; an org's
 * getting-started list at Simple level → `org_member`, at Medio and up →
 * `org_contributor` (which also earns `org_member`: its list contains the
 * Simple one). The server re-checks the steps it can see before awarding.
 */
export type BadgeKind = "ready" | "org_member" | "org_contributor";

export interface BadgeOrg {
  id: string;
  slug: string;
  name: string;
}

export interface MemberBadge {
  kind: BadgeKind;
  /** null for `ready`; the org it was earned in otherwise. */
  org: BadgeOrg | null;
  earnedAt: string;
}

/** Which badge finishing an org list at this tier claims. */
export function orgBadgeKind(tier: "simple" | "contributor"): BadgeKind {
  return tier === "contributor" ? "org_contributor" : "org_member";
}

const RANK: Record<BadgeKind, number> = { ready: 0, org_contributor: 1, org_member: 2 };

/**
 * What a profile shows: `ready` first, then one badge per org — the
 * contributor one wins over the member one it implies — orgs by name.
 */
export function badgesForDisplay(badges: readonly MemberBadge[]): MemberBadge[] {
  const contributorOrgs = new Set(
    badges.filter((b) => b.kind === "org_contributor" && b.org).map((b) => b.org!.id),
  );
  return badges
    .filter((b) => !(b.kind === "org_member" && b.org && contributorOrgs.has(b.org.id)))
    .sort(
      (a, b) =>
        RANK[a.kind] - RANK[b.kind] ||
        (a.org?.name ?? "").localeCompare(b.org?.name ?? "", undefined, { sensitivity: "base" }),
    );
}
